import React from 'react';
import HOT from '../../assets/images/Hotesse2.png';
import AZ2 from '../../assets/logos/LOG.png';
import AZD from '../../assets/logos/AL.png';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// ---- Palette dorée dégradée ----
const colors = {
  goldLight: '#F5E1C8',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  textDark: '#3B2F2A',
  textSoft: '#5C4A3D',
  whiteGlass: 'rgba(255, 255, 255, 0.75)',
  borderGold: 'rgba(198, 156, 109, 0.3)',
};

// Variantes d'animation
const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } },
  hover: { scale: 1.03 }
};

const ServiceSection = () => {
  const servicesLeft = [
    { to: '/LifeStyleEvasion', title: 'Lifestyle / Évasion', desc: 'Votre style de vie, notre inspiration.' },
    { to: '/Bien-etre', title: 'Bien-Être', desc: 'Un esprit apaisé dans un corps choyé.' },
    { to: '/Conciergerie-privee', title: 'Conciergerie Privée', desc: 'L’exclusivité au service de votre quotidien.' },
  ];

  const servicesRight = [
    { to: '/Culture-tourisme', title: 'Culture / Tourisme', desc: 'Tourisme d’exception pour esprits curieux.' },
    { to: '/Formalites-aeroportuaire', title: 'Formalités Aéroportuaires', desc: 'Voyagez l’esprit libre, on s’occupe du reste.' },
    { to: '/Lounges', title: 'Lounge VIP & Espace de Travail', desc: 'L’alliance parfaite entre confort et productivité.' },
  ];

  return (
    <>
      <style>{`
        .service-section-gold {
          position: relative;
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 30%, #C69C6D 60%, #E8CFB5 100%);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
          padding: 80px 0;
          overflow: hidden;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Orbes décoratives dorées */
        .service-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.5;
          z-index: 0;
        }
        .orb-1 {
          width: 40vw; height: 40vw;
          background: ${colors.goldLight};
          top: -10%; left: -5%;
          animation: floatOrb 20s ease-in-out infinite;
        }
        .orb-2 {
          width: 35vw; height: 35vw;
          background: #F5E1C8;
          bottom: -10%; right: -5%;
          animation: floatOrb 18s ease-in-out infinite alternate-reverse;
        }
        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        /* Carte de service en glassmorphism */
        .service-card {
          background: ${colors.whiteGlass};
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border: 1px solid ${colors.borderGold};
          border-radius: 20px;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          box-shadow: 0 8px 24px rgba(107, 90, 70, 0.08);
          transition: all 0.3s ease;
          cursor: default;
        }
        .service-card:hover {
          transform: translateY(-4px);
          background: rgba(255, 255, 255, 0.85);
          border-color: ${colors.gold};
          box-shadow: 0 12px 28px rgba(184, 138, 86, 0.18);
        }

        .service-icon {
          width: 56px;
          height: 56px;
          object-fit: contain;
          filter: drop-shadow(0 4px 6px rgba(0,0,0,0.1));
        }

        .service-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: ${colors.textDark};
          margin-bottom: 4px;
          letter-spacing: -0.2px;
        }
        .service-title a {
          color: inherit;
          text-decoration: none;
          transition: color 0.2s;
        }
        .service-title a:hover {
          color: ${colors.goldDark};
        }
        .service-desc {
          font-size: 0.9rem;
          color: ${colors.textSoft};
          line-height: 1.5;
          margin: 0;
        }

        /* Image centrale hôtesse */
        .hostess-img {
          width: 100%;
          max-width: 380px;
          height: auto;
          border-radius: 28px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
          transition: transform 0.3s;
          object-fit: cover;
          aspect-ratio: 3/4;
          background: linear-gradient(135deg, ${colors.goldLight}, ${colors.gold});
          padding: 6px;
          background-clip: content-box;
        }

        @media (max-width: 992px) {
          .service-section-gold {
            padding: 60px 0;
          }
          .hostess-img {
            max-width: 300px;
            margin: 0 auto 30px;
          }
          .service-card {
            padding: 16px 18px;
            gap: 14px;
          }
        }
      `}</style>

      <motion.section
        className="service-section-gold"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        {/* Orbes dorées */}
        <div className="service-orb orb-1"></div>
        <div className="service-orb orb-2"></div>

        <div className="container position-relative" style={{ zIndex: 2 }}>
          {/* Logo principal */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={imageVariants}
            viewport={{ once: true }}
            className="text-center mb-4"
          >
            <img
              loading="lazy"
              src={AZ2}
              alt="Azyekos Logo"
              style={{ width: 'min(600px, 90%)', filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.1))' }}
              className="img-fluid"
            />
          </motion.div>

          <div className="text-center mb-5">
            <h3
              style={{
                fontSize: 'clamp(1.5rem, 3.5vw, 2rem)',
                fontWeight: 600,
                color: colors.textDark,
                letterSpacing: '1px',
              }}
            >
              DÉCOUVREZ NOS SERVICES
            </h3>
            <div
              style={{
                width: '80px',
                height: '3px',
                background: colors.gold,
                margin: '8px auto 0',
                borderRadius: '2px',
              }}
            ></div>
          </div>

          {/* Grille de services */}
          <motion.div
            className="row align-items-center"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {/* Colonne gauche */}
            <div className="col-lg-4 mb-4 mb-lg-0">
              {servicesLeft.map((service, idx) => (
                <motion.div key={idx} className="mb-3" variants={itemVariants}>
                  <div className="service-card">
                    <img loading="lazy" src={AZD} alt="" className="service-icon" />
                    <div>
                      <h4 className="service-title">
                        <Link to={service.to}>{service.title}</Link>
                      </h4>
                      <p className="service-desc">{service.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Image hôtesse centrale */}
            <motion.div
              className="col-lg-4 text-center mb-4 mb-lg-0"
              variants={imageVariants}
              whileHover="hover"
            >
              <img
                loading="lazy"
                src={HOT}
                alt="Notre hôtesse"
                className="hostess-img"
              />
            </motion.div>

            {/* Colonne droite */}
            <div className="col-lg-4">
              {servicesRight.map((service, idx) => (
                <motion.div key={idx} className="mb-3" variants={itemVariants}>
                  <div className="service-card">
                    <img loading="lazy" src={AZD} alt="" className="service-icon" />
                    <div>
                      <h4 className="service-title">
                        <Link to={service.to}>{service.title}</Link>
                      </h4>
                      <p className="service-desc">{service.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default ServiceSection;